import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../images/logo.jpeg';
const Sidebar = ({ onCloseSidebar }) => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const sidebarRef = useRef(null);

  const isActive = (path) => location.pathname === path;
  const isActiveGroup = (paths) => paths.some(path => location.pathname.includes(path));

  // Auto-expand menus when their submenu is active
  useEffect(() => {
    const menuGroups = {
      admin: ['view-users', 'view-vendors', 'vendor-verification', 'vendor-offer-list', 'vendor-unlock-list', 'vendor-subscription-list', 'create-transaction', 'bank-proof', 'direct-payment', 'testing-account', 'view-bookings', 'add-booking', 'booking-detail', 'bookings', 'booking/create', 'view-coupons', 'coupon', 'view-influencers', 'view-settings', 'view-vendor-application', 'vendor/edit', 'autobuy-list'],
      callManagement: ['call-logs'],
      caller: ['caller-vendors', 'caller-travel-post', 'caller-pending-transactions'],
      locations: ['country', 'add-country', 'location', 'add-location', 'edit-location', 'state', 'add-state', 'edit-state', 'add-city', 'edit-country', 'view-months', 'add-month', 'edit-month', 'city', 'things-to-do', 'add-things-to-do', 'edit-things-to-do'],
      packages: ['view-packages', 'package', 'package-facility'],
      dmcManage: ['view-dmcs', 'dmc/create', 'dmc/edit', 'view-dmc-itineraries', 'dmc-itinerary/create', 'dmc-itinerary/edit'],
      hotels: ['hotel', 'view-amenity', 'view-hoteltype'],
      email: ['send-mail', 'email-template-generator', 'email-template'],
      webSettings: ['view-offers', 'add-offer', 'edit-offer', 'view-rewards', 'view-winners', 'view-whatsnew', 'view-common-settings', 'reward-prizes'],
      travelPost: ['travel-post', 'max-offer-list', 'view-proposal', 'replacement-list', 'unlocked-list', 'proposal-list', 'max-unlocked-list', 'lead-analytics']
    };

    const newExpandedMenus = {};
    Object.keys(menuGroups).forEach(menuKey => {
      if (isActiveGroup(menuGroups[menuKey])) {
        newExpandedMenus[menuKey] = true;
      }
    });

    setExpandedMenus(prev => ({ ...prev, ...newExpandedMenus }));
  }, [location.pathname]);

  // Auto-scroll to active menu item
  useEffect(() => {
    const timer = setTimeout(() => {
      if (sidebarRef.current) {
        const activeElement = sidebarRef.current.querySelector('.bg-\\[\\#e8f4fd\\]');
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, expandedMenus]);

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      [menuKey]: !prev[menuKey]
    }));
  };

  // Helper function to check if user has service
  const hasService = (service) => {
    return user?.services?.includes(service);
  };

  // Helper function for super admin - show all menus
  const isSuperAdmin = () => {
    return user?.role === 'admin';
  };

  // Enhanced permission check for super admin
  const canAccess = (module, subModule, action) => {
    const isAdmin = isSuperAdmin();
    if (isAdmin) return true;
    return hasPermission(module, subModule, action);
  };

  return (
    <aside ref={sidebarRef} className="w-64 bg-white shadow-lg border-r border-orange-200 h-full overflow-y-auto">
      {/* Sidebar Header */}
      <div className="p-2 border-b border-orange-200">
        <Link to="/dashboard" className="flex items-center justify-center group no-underline">
          <img
            src={logo}
            alt="TravellingLark Logo"
            className="h-10 w-auto object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        </Link>
      </div>

      {/* Sidebar Navigation */}
      <nav className="p-4 space-y-2" onClick={(e) => {
        if (e.target.closest('a') && window.innerWidth < 1024 && onCloseSidebar) {
          onCloseSidebar();
        }
      }}>
        {/* Dashboard */}
        <Link
          to="/dashboard"
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/dashboard')
            ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
            }`}
        >
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/dashboard')
            ? 'bg-[#d1e9f8] text-[#009ef7]'
            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
            }`}>
            <i className="bi bi-house text-xs"></i>
          </div>
          <span className="text-sm font-medium">Dashboard</span>
        </Link>

        {/* Admin Section */}
        {(user?.role === 'admin' || canAccess('admin_page', 'admin_page', 'view')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.admin || isActiveGroup(['view-users', 'view-vendors', 'vendor-verification', 'vendor-offer-list', 'vendor-unlock-list', 'vendor-subscription-list', 'create-transaction', 'bank-proof', 'direct-payment', 'testing-account', 'view-bookings', 'add-booking', 'booking-detail', 'bookings', 'booking/create', 'view-coupons', 'coupon', 'view-influencers', 'view-settings', 'view-vendor-application', 'vendor/edit', 'autobuy-list'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('admin')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.admin || isActiveGroup(['view-users', 'view-vendors', 'vendor-verification', 'vendor-offer-list', 'vendor-unlock-list', 'vendor-subscription-list', 'create-transaction', 'bank-proof', 'direct-payment', 'testing-account', 'view-bookings', 'add-booking', 'booking-detail', 'bookings', 'booking/create', 'view-coupons', 'coupon', 'view-influencers', 'view-settings', 'view-vendor-application', 'vendor/edit', 'autobuy-list'])
                  ? 'bg-orange-100 text-orange-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-person-gear text-xs"></i>
                </div>
                <span className="text-sm font-medium">Admin</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.admin ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.admin && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                {canAccess('admin_page', 'user', 'view') && (
                  <Link
                    to="/view-users"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-users')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-users')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-people text-xs"></i>
                    </div>
                    <span className="text-xs">Users</span>
                  </Link>
                )}
                {canAccess('admin_page', 'vendor', 'view') && (
                  <Link
                    to="/view-vendors"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['view-vendors', 'view-vendor-application', 'vendor-subscription-list', 'vendor/edit'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['view-vendors', 'view-vendor-application', 'vendor-subscription-list', 'vendor/edit'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-shop text-xs"></i>
                    </div>
                    <span className="text-xs">Vendors</span>
                  </Link>
                )}
                {canAccess('admin_page', 'testing_account', 'view') && (
                  <Link
                    to="/testing-account"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/testing-account')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/testing-account')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-bug text-xs"></i>
                    </div>
                    <span className="text-xs">Testing Account</span>
                  </Link>
                )}
                {(isSuperAdmin() || canAccess('admin_page', 'booking', 'view')) && (
                  <Link
                    to="/view-bookings"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['view-bookings', 'add-booking', 'booking-detail', 'bookings', 'booking/create'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['view-bookings', 'add-booking', 'booking-detail', 'bookings', 'booking/create'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-journal-check text-xs"></i>
                    </div>
                    <span className="text-xs">Booking Manage</span>
                  </Link>
                )}
                {canAccess('admin_page', 'bankProof', 'view') && (
                  <Link
                    to="/bank-proof"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/bank-proof')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/bank-proof')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-bank text-xs"></i>
                    </div>
                    <span className="text-xs">Bank Deposit Request</span>
                  </Link>
                )}
                {canAccess('admin_page', 'directPayment', 'view') && (
                  <Link
                    to="/direct-payment"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/direct-payment')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/direct-payment')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-cash-coin text-xs"></i>
                    </div>
                    <span className="text-xs">Direct Payments</span>
                  </Link>
                )}
                {canAccess('admin_page', 'coupons', 'view') && (
                  <Link
                    to="/view-coupons"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['view-coupons', 'coupon'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['view-coupons', 'coupon'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-ticket-perforated text-xs"></i>
                    </div>
                    <span className="text-xs">Coupons</span>
                  </Link>
                )}
                {canAccess('admin_page', 'influencers', 'view') && (
                  <Link
                    to="/view-influencers"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-influencers')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-influencers')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-star text-xs"></i>
                    </div>
                    <span className="text-xs">Influencers</span>
                  </Link>
                )}
                {canAccess('admin_page', 'autobuy', 'view') && (
                  <Link
                    to="/autobuy-list"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/autobuy-list')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/autobuy-list')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-lightning-charge text-xs"></i>
                    </div>
                    <span className="text-xs">AutoBuy</span>
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {/* Travel Leads */}
        {(user?.role === 'admin' || canAccess('manage_travel_post', 'manage_travel_post', 'view')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.travelPost || isActiveGroup(['travel-post', 'max-offer-list', 'view-proposal', 'replacement-list', 'unlocked-list', 'proposal-list', 'max-unlocked-list', 'lead-analytics'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('travelPost')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.travelPost || isActiveGroup(['travel-post', 'max-offer-list', 'view-proposal', 'replacement-list', 'unlocked-list', 'proposal-list', 'max-unlocked-list', 'lead-analytics'])
                  ? 'bg-indigo-100 text-indigo-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-signpost-2 text-xs"></i>
                </div>
                <span className="text-sm font-medium">Travel Leads</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.travelPost ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.travelPost && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                {canAccess('manage_travel_post', 'travel_post', 'view') && (
                  <Link
                    to="/travel-post"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['travel-post', 'view-proposal'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['travel-post', 'view-proposal'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-signpost text-xs"></i>
                    </div>
                    <span className="text-xs">Travel Leads</span>
                  </Link>
                )}
                {canAccess('manage_travel_post', 'unlock_list', 'view') && (
                  <Link
                    to="/unlocked-list"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/unlocked-list')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/unlocked-list')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-unlock text-xs"></i>
                    </div>
                    <span className="text-xs">Unlock List</span>
                  </Link>
                )}
                {canAccess('manage_travel_post', 'max_unlocked', 'view') && (
                  <Link
                    to="/max-unlocked-list"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/max-unlocked-list')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/max-unlocked-list')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-award text-xs"></i>
                    </div>
                    <span className="text-xs">Max Unlocked</span>
                  </Link>
                )}
                {canAccess('manage_travel_post', 'replacement_list', 'view') && (
                  <Link
                    to="/replacement-list"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/replacement-list')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/replacement-list')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-arrow-repeat text-xs"></i>
                    </div>
                    <span className="text-xs">Replacement List</span>
                  </Link>
                )}
                {canAccess('manage_travel_post', 'lead_analytics', 'view') && (
                  <Link
                    to="/lead-analytics"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/lead-analytics')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/lead-analytics')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-bar-chart-line text-xs"></i>
                    </div>
                    <span className="text-xs">Lead Analytics</span>
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {/* Call Management */}
        {(user?.role === 'admin' || canAccess('call_management', 'call_management', 'view')) && (
          <Link
            to="/call-logs"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActiveGroup(['call-logs'])
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActiveGroup(['call-logs'])
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-telephone text-xs"></i>
            </div>
            <span className="text-sm font-medium">Call Management</span>
          </Link>
        )}

        {/* Web Settings */}
        {(user?.role === 'admin' || canAccess('web_settings', 'web_settings', 'view') || hasPermission('web_settings', 'common_settings', 'view') || hasPermission('web_settings', 'static_page_meta_data', 'view')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.webSettings || isActiveGroup(['view-offers', 'add-offer', 'edit-offer', 'view-rewards', 'view-winners', 'view-whatsnew', 'view-common-settings', 'reward-prizes', 'static-page-meta-data'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('webSettings')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.webSettings || isActiveGroup(['view-offers', 'add-offer', 'edit-offer', 'view-rewards', 'view-winners', 'view-whatsnew', 'view-common-settings', 'reward-prizes', 'static-page-meta-data'])
                  ? 'bg-cyan-100 text-cyan-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-gear text-xs"></i>
                </div>
                <span className="text-sm font-medium">Web Setting</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.webSettings ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.webSettings && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                {hasPermission('web_settings', 'offers', 'view') && (
                  <Link
                    to="/view-offers"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['view-offers', 'add-offer', 'edit-offer'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['view-offers', 'add-offer', 'edit-offer'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-percent text-xs"></i>
                    </div>
                    <span className="text-xs">Offers</span>
                  </Link>
                )}
                {hasPermission('web_settings', 'whatsnew', 'view') && (
                  <Link
                    to="/view-whatsnew"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-whatsnew')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-whatsnew')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-newspaper text-xs"></i>
                    </div>
                    <span className="text-xs">What's New</span>
                  </Link>
                )}
                {hasPermission('web_settings', 'common_settings', 'view') && (
                  <Link
                    to="/view-common-settings"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-common-settings')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-common-settings')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-sliders text-xs"></i>
                    </div>
                    <span className="text-xs">Common Settings</span>
                  </Link>
                )}
                {(user?.role === 'admin' || hasPermission('web_settings', 'static_page_meta_data', 'view')) && (
                  <Link
                    to="/static-page-meta-data"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/static-page-meta-data')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/static-page-meta-data')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-file-earmark-text text-xs"></i>
                    </div>
                    <span className="text-xs">StaticPage Meta Data</span>
                  </Link>
                )}
                {user?.role === 'admin' && (
                  <>
                    <Link
                      to="/view-rewards"
                      className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-rewards')
                        ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-rewards')
                        ? 'bg-[#d1e9f8] text-[#009ef7]'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                        }`}>
                        <i className="bi bi-gift text-xs"></i>
                      </div>
                      <span className="text-xs">Rewards Application</span>
                    </Link>
                    <Link
                      to="/view-winners"
                      className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-winners')
                        ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-winners')
                        ? 'bg-[#d1e9f8] text-[#009ef7]'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                        }`}>
                        <i className="bi bi-trophy text-xs"></i>
                      </div>
                      <span className="text-xs">Reward Winners</span>
                    </Link>
                    <Link
                      to="/reward-prizes"
                      className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/reward-prizes')
                        ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/reward-prizes')
                        ? 'bg-[#d1e9f8] text-[#009ef7]'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                        }`}>
                        <i className="bi bi-award text-xs"></i>
                      </div>
                      <span className="text-xs">Reward Prizes</span>
                    </Link>
                    <Link
                      to="/recharge-plans"
                      className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/recharge-plans')
                        ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/recharge-plans')
                        ? 'bg-[#d1e9f8] text-[#009ef7]'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                        }`}>
                        <i className="bi bi-credit-card text-xs"></i>
                      </div>
                      <span className="text-xs">Recharge Plans</span>
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Locations */}
        {(user?.role === 'admin' || canAccess('location_page', 'location_page', 'view')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.locations || isActiveGroup(['country', 'add-country', 'location', 'add-location', 'edit-location', 'state', 'add-state', 'edit-state', 'add-city', 'edit-country', 'view-months', 'add-month', 'edit-month', 'city', 'things-to-do'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('locations')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.locations || isActiveGroup(['country', 'add-country', 'location', 'add-location', 'edit-location', 'state', 'add-state', 'edit-state', 'add-city', 'edit-country', 'view-months', 'add-month', 'edit-month', 'city', 'things-to-do'])
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-geo-alt text-xs"></i>
                </div>
                <span className="text-sm font-medium">Locations</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.locations ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.locations && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                {hasPermission('location_page', 'country', 'view') && (
                  <Link
                    to="/country"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['country', 'add-country', 'edit-country', 'state', 'add-state', 'edit-state'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['country', 'add-country', 'edit-country', 'state', 'add-state', 'edit-state'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-flag text-xs"></i>
                    </div>
                    <span className="text-xs">Country</span>
                  </Link>
                )}
                {hasPermission('location_page', 'location', 'view') && (
                  <Link
                    to="/location"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['location', 'add-location', 'edit-location', 'things-to-do'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['location', 'add-location', 'edit-location', 'things-to-do'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-pin-map text-xs"></i>
                    </div>
                    <span className="text-xs">Location</span>
                  </Link>
                )}
                {!hasService('locations') && hasPermission('location_page', 'city', 'view') && (
                  <Link
                    to="/add-city"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/add-city')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/add-city')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-building text-xs"></i>
                    </div>
                    <span className="text-xs">Add Bulk Locations</span>
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {/* Blogs */}
        {(isSuperAdmin() || user?.role === 'admin' || hasService('blog') || canAccess('blog', 'blog', 'view')) && (
          <Link
            to="/view-blogs"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActiveGroup(['view-blogs', 'add-blog', 'edit-blog'])
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActiveGroup(['view-blogs', 'add-blog', 'edit-blog'])
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-journal-text text-xs"></i>
            </div>
            <span className="text-sm font-medium">Blogs</span>
          </Link>
        )}

        {/* Packages */}
        {(isSuperAdmin() || user?.role === 'admin' || hasService('packages') || canAccess('package_page', 'package_page', 'view')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.packages || isActiveGroup(['view-packages', 'package', 'package-facility'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('packages')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.packages || isActiveGroup(['view-packages', 'package', 'package-facility'])
                  ? 'bg-pink-100 text-pink-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-suitcase text-xs"></i>
                </div>
                <span className="text-sm font-medium">Package</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.packages ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.packages && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                {(isSuperAdmin() || hasService('packages') || canAccess('package_page', 'package_facility', 'view')) && (
                  <Link
                    to="/package-facility"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/package-facility')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/package-facility')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-list-check text-xs"></i>
                    </div>
                    <span className="text-xs">Travel Facility</span>
                  </Link>
                )}
                {(isSuperAdmin() || hasService('packages') || canAccess('package_page', 'package', 'view')) && (
                  <Link
                    to="/view-packages"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['view-packages', 'package'])
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['view-packages', 'package'])
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-box-seam text-xs"></i>
                    </div>
                    <span className="text-xs">Package</span>
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {/* DMC Manage */}
        {isSuperAdmin() && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.dmcManage || isActiveGroup(['view-dmcs', 'dmc/create', 'dmc/edit', 'view-dmc-itineraries', 'dmc-itinerary/create', 'dmc-itinerary/edit'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('dmcManage')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.dmcManage || isActiveGroup(['view-dmcs', 'dmc/create', 'dmc/edit', 'view-dmc-itineraries', 'dmc-itinerary/create', 'dmc-itinerary/edit'])
                  ? 'bg-cyan-100 text-cyan-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-diagram-3 text-xs"></i>
                </div>
                <span className="text-sm font-medium">DMC Manage</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.dmcManage ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.dmcManage && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                <Link
                  to="/view-dmcs"
                  className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['view-dmcs', 'dmc/create', 'dmc/edit'])
                    ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['view-dmcs', 'dmc/create', 'dmc/edit'])
                    ? 'bg-[#d1e9f8] text-[#009ef7]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}>
                    <i className="bi bi-buildings text-xs"></i>
                  </div>
                  <span className="text-xs">DMC</span>
                </Link>
                <Link
                  to="/view-dmc-itineraries"
                  className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['view-dmc-itineraries', 'dmc-itinerary/create', 'dmc-itinerary/edit'])
                    ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['view-dmc-itineraries', 'dmc-itinerary/create', 'dmc-itinerary/edit'])
                    ? 'bg-[#d1e9f8] text-[#009ef7]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}>
                    <i className="bi bi-journal-text text-xs"></i>
                  </div>
                  <span className="text-xs">DMC Itinerary</span>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Contact Us */}
        {(user?.role === 'admin' || canAccess('contactus', 'contactus', 'view')) && (
          <Link
            to="/view-contactus"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-contactus')
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-contactus')
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-envelope-open text-xs"></i>
            </div>
            <span className="text-sm font-medium">Contact Us</span>
          </Link>
        )}

        {/* Corporate Tours */}
        {(user?.role === 'admin' || isSuperAdmin() || canAccess('corporate', 'corporate', 'view') || canAccess('corporate_inquiry', 'corporate_inquiry', 'view')) && (
          <Link
            to="/view-corporate-inquiries"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-corporate-inquiries')
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-corporate-inquiries')
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-briefcase text-xs"></i>
            </div>
            <span className="text-sm font-medium">Corporate Tours</span>
          </Link>
        )}

        {/* IT Support */}
        {(user?.role === 'admin' || canAccess('itsupport', 'itsupport', 'view')) && (
          <Link
            to="/view-itsupport"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-itsupport')
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-itsupport')
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-headset text-xs"></i>
            </div>
            <span className="text-sm font-medium">IT Support</span>
          </Link>
        )}

        {/* Order List */}
        {user?.role === 'admin' && (
          <Link
            to="/order-list"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/order-list')
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/order-list')
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-cart-check text-xs"></i>
            </div>
            <span className="text-sm font-medium">Order List</span>
          </Link>
        )}

        {/* Admin Only Features */}
        {user?.role === 'admin' && (
          <>
            {canAccess('banner', 'banner', 'view') && (
              <Link
                to="/view-banners"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-banners')
                  ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-banners')
                  ? 'bg-[#d1e9f8] text-[#009ef7]'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-image text-xs"></i>
                </div>
                <span className="text-sm font-medium">Banner</span>
              </Link>
            )}

            <Link
              to="/view-gallery"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-gallery')
                ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-gallery')
                ? 'bg-[#d1e9f8] text-[#009ef7]'
                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                }`}>
                <i className="bi bi-images text-xs"></i>
              </div>
              <span className="text-sm font-medium">Wall Gallery</span>
            </Link>

            <Link
              to="/view-suggestions"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-suggestions')
                ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-suggestions')
                ? 'bg-[#d1e9f8] text-[#009ef7]'
                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                }`}>
                <i className="bi bi-lightbulb text-xs"></i>
              </div>
              <span className="text-sm font-medium">Suggestions</span>
            </Link>

            <Link
              to="/chats"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/chats')
                ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/chats')
                ? 'bg-[#d1e9f8] text-[#009ef7]'
                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                }`}>
                <i className="bi bi-chat text-xs"></i>
              </div>
              <span className="text-sm font-medium">Chat</span>
            </Link>

            <Link
              to="/testimonial"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/testimonial')
                ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/testimonial')
                ? 'bg-[#d1e9f8] text-[#009ef7]'
                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                }`}>
                <i className="bi bi-star text-xs"></i>
              </div>
              <span className="text-sm font-medium">Testimonial</span>
            </Link>

            <Link
              to="/reviews"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/reviews')
                ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/reviews')
                ? 'bg-[#d1e9f8] text-[#009ef7]'
                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                }`}>
                <i className="bi bi-chat-square-text text-xs"></i>
              </div>
              <span className="text-sm font-medium">Reviews</span>
            </Link>

            <Link
              to="/web-privacy"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/web-privacy')
                ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/web-privacy')
                ? 'bg-[#d1e9f8] text-[#009ef7]'
                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                }`}>
                <i className="bi bi-shield-lock text-xs"></i>
              </div>
              <span className="text-sm font-medium">Web Privacy</span>
            </Link>
          </>
        )}

        {/* Callback Requests */}
        {(isSuperAdmin() || hasService('leads') || user?.role === 'vendor' || canAccess('callback', 'callback', 'view')) && (
          <Link
            to="/view-leads"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-leads')
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-leads')
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-chat-dots text-xs"></i>
            </div>
            <span className="text-sm font-medium">Callback Requests</span>
          </Link>
        )}

        {/* Caller Menu */}
        {(user?.role === 'admin' || canAccess('caller_page', 'caller_page', 'view')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.caller || isActiveGroup(['caller-vendors', 'caller-travel-post', 'caller-pending-transactions'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('caller')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.caller || isActiveGroup(['caller-vendors', 'caller-travel-post', 'caller-pending-transactions'])
                  ? 'bg-purple-100 text-purple-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-person-gear text-xs"></i>
                </div>
                <span className="text-sm font-medium">Caller Menu</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.caller ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.caller && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                {canAccess('caller_page', 'vendor', 'view') && (
                  <Link
                    to="/caller-vendors"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/caller-vendors')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/caller-vendors')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-shop text-xs"></i>
                    </div>
                    <span className="text-xs">Vendors</span>
                  </Link>
                )}
                {canAccess('caller_page', 'travel_post', 'view') && (
                  <Link
                    to="/caller-travel-post"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/caller-travel-post')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/caller-travel-post')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-signpost text-xs"></i>
                    </div>
                    <span className="text-xs">Travel Leads</span>
                  </Link>
                )}
                {canAccess('caller_page', 'pending_transactions', 'view') && (
                  <Link
                    to="/caller-pending-transactions"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/caller-pending-transactions')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/caller-pending-transactions')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-clock text-xs"></i>
                    </div>
                    <span className="text-xs">Failed Transactions</span>
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {/* Staff */}
        {(isSuperAdmin() || ((user?.role === 'vendor' || user?.role === 'admin') && !hasService('locations') && !hasService('other') && !hasService('caller'))) && (
          <Link
            to="/staff"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActiveGroup(['staff'])
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActiveGroup(['staff'])
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-people text-xs"></i>
            </div>
            <span className="text-sm font-medium">Staff</span>
          </Link>
        )}

        {/* Trip Category */}
        {(user?.role === 'admin' || canAccess('category', 'category', 'view')) && (
          <Link
            to="/view-category"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/view-category')
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/view-category')
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-signpost text-xs"></i>
            </div>
            <span className="text-sm font-medium">Trip Category</span>
          </Link>
        )}

        {/* Hotels */}
        {(isSuperAdmin() || user?.role === 'admin' || hasService('hotels')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.hotels || isActiveGroup(['hotel', 'view-amenity', 'view-hoteltype'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('hotels')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.hotels || isActiveGroup(['hotel', 'view-amenity', 'view-hoteltype'])
                  ? 'bg-teal-100 text-teal-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-building text-xs"></i>
                </div>
                <span className="text-sm font-medium">Hotels</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.hotels ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.hotels && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                <Link
                  to="/view-amenity"
                  className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-amenity')
                    ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-amenity')
                    ? 'bg-[#d1e9f8] text-[#009ef7]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}>
                    <i className="bi bi-wifi text-xs"></i>
                  </div>
                  <span className="text-xs">Amenities</span>
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/view-hoteltype"
                    className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/view-hoteltype')
                      ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/view-hoteltype')
                      ? 'bg-[#d1e9f8] text-[#009ef7]'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                      }`}>
                      <i className="bi bi-tags text-xs"></i>
                    </div>
                    <span className="text-xs">Hotel Type</span>
                  </Link>
                )}
                <Link
                  to="/hotel"
                  className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActiveGroup(['hotel'])
                    ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActiveGroup(['hotel'])
                    ? 'bg-[#d1e9f8] text-[#009ef7]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}>
                    <i className="bi bi-building-check text-xs"></i>
                  </div>
                  <span className="text-xs">Hotel</span>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Vehicles */}
        {(isSuperAdmin() || user?.role === 'admin' || hasService('vehicles')) && (
          <>
            {user?.role === 'admin' && (
              <Link
                to="/vehicle-type"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/vehicle-type')
                  ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/vehicle-type')
                  ? 'bg-[#d1e9f8] text-[#009ef7]'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-truck text-xs"></i>
                </div>
                <span className="text-sm font-medium">Vehicle Type</span>
              </Link>
            )}
            <Link
              to="/view-vehicles"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActiveGroup(['view-vehicles', 'vehicle'])
                ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActiveGroup(['view-vehicles', 'vehicle'])
                ? 'bg-[#d1e9f8] text-[#009ef7]'
                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                }`}>
                <i className="bi bi-car-front-fill text-xs"></i>
              </div>
              <span className="text-sm font-medium">Vehicle</span>
            </Link>
          </>
        )}

        {/* Email */}
        {(user?.role === 'admin' || canAccess('email', 'email', 'view')) && (
          <div className="space-y-1">
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group no-underline ${expandedMenus.email || isActiveGroup(['send-mail', 'email-template-generator', 'email-template'])
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              onClick={() => toggleMenu('email')}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${expandedMenus.email || isActiveGroup(['send-mail', 'email-template-generator', 'email-template'])
                  ? 'bg-red-100 text-red-600'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                  <i className="bi bi-envelope text-xs"></i>
                </div>
                <span className="text-sm font-medium">Email</span>
              </div>
              <i className={`bi bi-chevron-down transition-transform ${expandedMenus.email ? 'rotate-180' : ''}`}></i>
            </div>

            {expandedMenus.email && (
              <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
                <Link
                  to="/send-mail"
                  className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/send-mail')
                    ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/send-mail')
                    ? 'bg-[#d1e9f8] text-[#009ef7]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}>
                    <i className="bi bi-send text-xs"></i>
                  </div>
                  <span className="text-xs">Send Email</span>
                </Link>
                <Link
                  to="/email-template-generator"
                  className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/email-template-generator')
                    ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/email-template-generator')
                    ? 'bg-[#d1e9f8] text-[#009ef7]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}>
                    <i className="bi bi-file-earmark-code text-xs"></i>
                  </div>
                  <span className="text-xs">Template Generator</span>
                </Link>
                <Link
                  to="/email-template"
                  className={`flex items-center space-x-3 px-3 py-1.5 rounded-lg text-xs transition-colors group no-underline ${isActive('/email-template')
                    ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isActive('/email-template')
                    ? 'bg-[#d1e9f8] text-[#009ef7]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}>
                    <i className="bi bi-file-earmark-text text-xs"></i>
                  </div>
                  <span className="text-xs">Send Template</span>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Notification */}
        {(user?.role === 'admin' || canAccess('notification', 'notification', 'view')) && (
          <Link
            to="/send-notification"
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group no-underline ${isActive('/send-notification')
              ? 'bg-[#e8f4fd] text-[#009ef7] border-l-4 border-[#009ef7]'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive('/send-notification')
              ? 'bg-[#d1e9f8] text-[#009ef7]'
              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}>
              <i className="bi bi-bell text-xs"></i>
            </div>
            <span className="text-sm font-medium">Notification</span>
          </Link>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
